import React, { useCallback } from 'react'
import cx from 'classnames'
import { Link, l, Popover } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { openManageProfilesModal as openManageProfilesModalAction } from 'common/core/actions/uiActions'
import EmailVerificationModal from 'common/core/components/EmailVerification/EmailVerificationModal'
import { getUser } from 'common/core/selectors/appSelectors'
import { CircleAvatar } from 'components/CircleAvatar'
import { ExternalPaths } from 'utils/router/constants'

import { EditEmailModal } from './components/EditEmailModal/EditEmailModal'
import { LanguageSelect } from './components/LanguageSelect/LanguageSelect'
import { ProfileSections } from './components/ProfileSections/ProfileSections'
import { SignInOptions } from './components/SignInOptions/SignInOptions'
import { VerifyEmailWarning } from './components/VerifyEmailWarning/VerifyEmailWarning'
import { useAgencyData } from './hooks/useAgencyData'
import { MyProfilePopoverAnalytics } from './lib/analytics'

import cm from './MyProfileLinkPopover.module.css'

interface MyProfileLinkPopoverProps {
  onClose: () => void
  anchor: HTMLElement | null
  isOpen: boolean
}

export const MyProfileLinkPopover = ({ onClose, anchor, isOpen }: MyProfileLinkPopoverProps) => {
  const dispatch = useAppDispatch()
  const [isEditEmailModalOpen, setIsEditEmailModalOpen] = React.useState(false)
  const [isVerificationModalOpen, setIsVerificationModalOpen] = React.useState(false)
  const isPopoverShown = isOpen && Boolean(anchor)
  const { email, email_unverified, full_name, avatar_large } = useAppSelector(getUser)
  const userEmail = email_unverified || email
  const { agencyId } = useAgencyData()

  const handleEditEmailClick = useCallback(() => {
    MyProfilePopoverAnalytics.logEditEmailClick()
    onClose()
    setIsEditEmailModalOpen(true)
  }, [onClose])

  const closeEditEmailModal = useCallback(() => {
    setIsEditEmailModalOpen(false)
  }, [])

  const openManageProfilesModal = useCallback(() => {
    MyProfilePopoverAnalytics.logOpenManageSignInModal()
    onClose()
    dispatch(openManageProfilesModalAction())
  }, [onClose, dispatch])

  const openVerificationModal = useCallback(() => {
    onClose()
    setIsVerificationModalOpen(true)
  }, [onClose])

  const closeVerificationModal = useCallback(() => {
    setIsVerificationModalOpen(false)
  }, [])

  const handleLogOutClick = () => {
    MyProfilePopoverAnalytics.logLogOutClick()
    onClose()
    window.location.href = ExternalPaths.LOGOUT
  }

  return (
    <>
      <Popover
        className={cm.wrapper}
        anchorOrigin="right center"
        popoverOrigin="left 90%"
        theme={{ base: 'm-l-xxs', arrow: 'm-l-xxs' }}
        arrow="right"
        open={isPopoverShown}
        anchor={anchor}
        onRequestClose={onClose}
        data-test-id="my-profile-link-popover"
      >
        <div id="my-profile-link-container">
          <div className="d-flex p-x-md p-t-md p-b-xs">
            <CircleAvatar className="m-r bg-default" size={56} url={avatar_large} />
            <div>
              <div title={full_name} className={cx(cm.fullName, 'text-heading-sm text-ellipsis')}>
                {full_name}
              </div>
              {userEmail && (
                <Link
                  contentColor="subtle"
                  variant="small"
                  rightIcon="Edit"
                  className={cm.emailText}
                  underline
                  title={userEmail}
                  onClick={handleEditEmailClick}
                >
                  {userEmail}
                </Link>
              )}
            </div>
          </div>
          <VerifyEmailWarning onOpenModal={openVerificationModal} />
          <ProfileSections agencyId={agencyId} />
        </div>
        <LanguageSelect />
        <div className="p-y-xs">
          <div
            className="d-flex p-x-md p-y-sm align-center justify-between bg-hover text-body-sm c-pointer"
            onClick={openManageProfilesModal}
          >
            <span className="m-r-sm">{l.translate('Add sign-in options')}</span>
            <SignInOptions />
          </div>
          <a
            onClick={handleLogOutClick}
            className="d-flex p-x-md p-y-sm bg-hover text-body-sm c-pointer"
          >
            {l.translate('Log out')}
          </a>
        </div>
      </Popover>
      {userEmail && (
        <>
          <EditEmailModal
            email={userEmail}
            isOpen={isEditEmailModalOpen}
            onClose={closeEditEmailModal}
            onUpdate={openVerificationModal}
          />
        </>
      )}
      <EmailVerificationModal open={isVerificationModalOpen} onClose={closeVerificationModal} />
    </>
  )
}
